import { Widget } from './Widget.js';

// core/EncodeValues.ts
// наверх файла, рядом с типами/хелперами
const p2 = (n) => (n < 10 ? '0' + n : '' + n);
function toIsoNoTz(d) {
    return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}T${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`;
}
/**
 * Собирает пары ключ-значение из DOM-узлов, компонент
 * или виджетов внутри указанного контейнера.
 *
 * Источники (по приоритету):
 *  1) Виджеты на элементе:
 *     - WithMultipleValues → берётся карта `getValues()`; null → "dbnull",
 *       массив → map(encodeScalarIfDateOrTime) → encodeArrayField, скаляр → encodeScalarIfDateOrTime.
 *     - WithMainValue → `getValue()` под ключом из `data-key` текущего элемента (те же правила).
 *  2) Атрибуты контейнера: `data-value` или `data-val` (строка как есть).
 *  3) Нативный контрол внутри узла:
 *     - radio → значение выбранной, иначе null;
 *     - checkbox → булево `checked`;
 *     - select[multiple] → массив выбранных значений → encodeArrayField;
 *     - select (single) → значение или null;
 *     - input/textarea → `value` (строка).
 * Преобразования:
 *  • null → "dbnull";
 *  • Date → локальный ISO без TZ (toIsoNoTz);
 *  • TimeOnly → "0001-01-01THH:mm:00";
 *  • Массивы → encodeArrayField.
 *
 * Поведение дубликатов:
 *  • Первое встретившееся значение по `data-key` побеждает; последующие не переписывают.
 *
 * Селектор:
 *  • Если `mask` не задан — используется `[data-key]`. Ошибка парсинга селектора даёт пустой результат.
 *
 * @param node Корневой элемент, внутри которого ищем.
 * @param mask CSS-селектор элементов (по умолчанию `[data-key]`).
 * @returns Объект с парами ключ-значение.
 */
function encodeValues(node, mask) {
    const out = {};
    // если маска не передана — берём [data-key]
    const selector = mask && mask.trim() !== '' ? mask : '[data-key]';
    let nodes;
    try {
        nodes = node.querySelectorAll(selector);
    }
    catch (e) {
        return out;
    }
    for (let i = 0; i < nodes.length; i++) {
        try {
            const el = nodes[i];
            // Подсчитываем начальное число ключей (контроль добавления)
            const wasLen = Object.keys(out).length;
            // Обрабатываем виджеты
            if (el.dataset.widget) {
                // пробуем найти виджет
                const widgets = Widget.allOn(el);
                for (const w of widgets) {
                    let handledByMultipleValues = false;
                    if (isWithMultipleValues(w)) {
                        // Виджет поддерживает мульти значение
                        // 2026-04-29: для составных виджетов сначала читаем карту значений целиком.
                        handledByMultipleValues = true;
                        const vals = w.getValues();
                        if (vals) {
                            for (const [k, v] of Object.entries(vals)) {
                                if (v === null) {
                                    out[k] = "dbnull";
                                }
                                else if (Array.isArray(v)) {
                                    // сначала конвертим элементы (Date/TimeOnly → ISO), затем кодируем массив
                                    const mapped = v.map(encodeScalarIfDateOrTime);
                                    out[k] = encodeArrayField(mapped);
                                }
                                else if (v !== undefined) {
                                    out[k] = encodeScalarIfDateOrTime(v);
                                }
                            }
                        }
                    }
                    if (!handledByMultipleValues && isWithMainValue(w)) {
                        // 2026-04-29: одиночное значение читаем только если виджет не дал карту значений.
                        const key = el.dataset.key;
                        if (key) {
                            const val = w.getValue();
                            if (val === null) {
                                out[key] = "dbnull";
                            }
                            else if (Array.isArray(val)) {
                                const mapped = val.map(encodeScalarIfDateOrTime);
                                out[key] = encodeArrayField(mapped);
                            }
                            else if (val !== undefined) {
                                out[key] = encodeScalarIfDateOrTime(val);
                            }
                        }
                    }
                }
            }
            // Компоненты
            // 1) Проверка что el это компонент - и получить его для работы как компонент (класс)
            // -> customElements.get(el);
            if (shouldSkipByClass(el))
                continue;
            const key = el.dataset.key;
            // Если ключа нет или ключ уже определен - продолжаем дальше
            if (!key || out[key])
                continue;
            // 1) Если есть data-value — используем его напрямую (аналог el.attr('data-value'))
            if (el.hasAttribute('data-value')) {
                out[key] = el.getAttribute('data-value');
                continue;
            }
            else if (el.hasAttribute('data-val')) {
                out[key] = el.getAttribute('data-val');
                continue;
            }
            // Если любые значения были получены по этому элементу ранее, то переходим к следующему
            if (Object.keys(out).length > wasLen)
                continue;
            // кодирование стандартными средствами html
            const ctrl = findNativeControl(el);
            if (ctrl) {
                const v = readDomValue(ctrl);
                if (v === null) {
                    out[key] = "dbnull";
                }
                else if (Array.isArray(v)) {
                    // Множественный select → массив значений
                    const mapped = v.map(encodeScalarIfDateOrTime);
                    out[key] = encodeArrayField(mapped);
                }
                else if (v !== undefined) {
                    out[key] = encodeScalarIfDateOrTime(v);
                }
            }
            else {
                // Резервный путь для текстовых контейнеров
                if (key && isContentTag(el)) {
                    const txt = readContentTagValue(el);
                    if (txt.trim() !== '') {
                        out[key] = txt;
                    }
                    else {
                        out[key] = null;
                    }
                }
                else {
                    // console.log('[encodeValues] Не найден контрол для data-key=', key, el);
                }
            }
        }
        catch (ex) {
            console.error('encodeValues', ex);
        }
    }
    return out;
}
/** Находим «нативный» контрол внутри элемента с data-key (или сам элемент) */
function findNativeControl(el) {
    if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
        return el;
    }
    return (el.querySelector('input') ||
        el.querySelector('select') ||
        el.querySelector('textarea') ||
        null);
}
/** Безопасное экранирование name для селектора */
function escName(name) {
    // CSS.escape есть не везде; делаем мягкий фолбэк
    const esc = typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape : null;
    return esc ? esc(name) : name.replace(/["\\\[\]\(\):]/g, "\\$&");
}
/** Извлекает значение по аналогии с jQuery .val() */
function readDomValue(ctrl) {
    if (ctrl instanceof HTMLInputElement) {
        const type = (ctrl.type || '').toLowerCase();
        if (type === 'radio') {
            // Для radio ищем выбранный элемент по имени в пределах формы (если есть), иначе — в документе
            const name = ctrl.name;
            if (!name) {
                // без name ведём себя как для одиночного элемента
                return ctrl.checked ? ctrl.value : null;
            }
            const scope = (ctrl.closest('form') ?? document);
            const checked = scope.querySelector(`input[type="radio"][name="${escName(name)}"]:checked`);
            return checked ? checked.value : null;
        }
        else if (type === 'checkbox') {
            // Возвращаем состояние чек бокса (true/false)
            return ctrl.checked;
        }
        else if (type === 'password') {
            // Пароль читается с trim()
            return ctrl.value.trim();
        }
        else if (type === 'button') {
            // input[type=button] не считается источником данных
            return undefined; // encodeValues пропустит ключ
        }
        // text/number/email/... → строка
        return ctrl.value;
    }
    if (ctrl instanceof HTMLSelectElement) {
        if (ctrl.multiple) {
            return Array.from(ctrl.selectedOptions).map(o => o.value);
        }
        // одиночный select:
        // - нет выбранной опции → null
        // - выбранная опция с пустым/пробельным value → null (дальше станет "dbnull")
        // - иначе → само значение
        if (ctrl.selectedIndex < 0)
            return null;
        const val = ctrl.value;
        return val.trim() === '' ? null : val;
    }
    // textarea
    return ctrl.value;
}
/** Проверка наличия интерфейса WithItemTitle */
function isWithItemTitle(x) {
    return !!x && typeof x.getItemTitle === "function";
}
/**
 * Проверяет, реализует ли объект контракт `WithMainValue`.
 * Используется как type guard для безопасного вызова `getValue` и `setValue`.
 *
 * @param x Произвольный объект для проверки
 * @returns true, если объект соответствует интерфейсу WithMainValue
 * для вызова `getValue`, `setValue` и `setValue`
 */
const isWithMainValue = (x) => !!x && typeof x.getValue === 'function'
    && typeof x.setValue === 'function'
    && typeof x.hasValue === "function";
/**
 * Проверяет, реализует ли объект контракт `WithValueTitle`.
 * Используется как type guard для безопасного вызова `getValueTitle`.
 *
 * @param x Произвольный объект для проверки
 * @returns true, если объект соответствует интерфейсу WithValueTitle
 */
const isWithValueTitle = (x) => !!x && typeof x.getValueTitle === 'function';
/**
 * Проверяет, реализует ли объект контракт `WithMultipleValues`.
 * Используется как type guard для безопасного вызова `getValues` и `setValues`.
 *
 * @param x Произвольный объект для проверки
 * @returns true, если объект соответствует интерфейсу WithMultipleValues
 */
const isWithMultipleValues = (x) => !!x && typeof x.getValues === 'function'
    && typeof x.setValues === 'function';
/**
 * Проверяет, реализует ли объект контракт `WithFilterEntries`.
 * 2026-04-29: это отдельный канал для составных виджетов, не зависящий от `getValue()`.
 */
const isWithFilterEntries = (x) => !!x && typeof x.getFilterEntries === 'function';
/**
 * Получение основного значения со сворачиванием до строки.
 * Поведение:
 *  - undefined → undefined (значение отсутствует)
 *  - null → null (сохраняем null, не "dbnull")
 *  - массив → кодируем в строку через encodeArrayField (с предварительной конвертацией дат/времени)
 *  - скаляр → конвертируем дату/время и приводим к строке
 */
function getMainValueStr(node) {
    const v = getMainValue(node); // unknown | unknown[] | null | undefined
    if (v === undefined || v === null)
        return v;
    if (Array.isArray(v)) {
        const mapped = v.map(encodeScalarIfDateOrTime);
        return encodeArrayField(mapped);
    }
    const scalar = encodeScalarIfDateOrTime(v);
    return String(scalar); // скаляр → строка
}
/**
 * Получить «основное значение» для элемента:
 * - перебирает все виджеты на элементе;
 * - если виджет `WithMainValue` → берёт `getValue()` и, если оно !== undefined, возвращает его;
 * - иначе, если виджет `WithMultipleValues` и у него есть `data-key`,
 *   то берёт `getValues()` и, если в нём есть такой ключ и значение !== undefined, возвращает его.
 * - null считается валидным значением (НЕ отфильтровывается).
 */
function getMainValue(node) {
    const widgets = Widget.allOn(node);
    for (const w of widgets) {
        // 1) Прямое «основное значение»
        if (isWithMainValue(w)) {
            const v = w.getValue();
            if (v !== undefined)
                return v; // null — допустим
        }
        // 2) Достаём по ключу из мульти-карты
        if (isWithMultipleValues(w)) {
            const key = node.dataset.key;
            if (!key)
                continue;
            const map = w.getValues();
            if (map) {
                if (Object.prototype.hasOwnProperty.call(map, key)) {
                    const mv = map[key];
                    if (mv !== undefined)
                        return mv; // null — допустим
                }
            }
        }
    }
    // Если есть data-value или data-val
    if (node.hasAttribute('data-value')) {
        return node.getAttribute('data-value');
    }
    else if (node.hasAttribute('data-val')) {
        return node.getAttribute('data-val');
    }
    if (shouldSkipByClass(node))
        return undefined;
    // Пробуем получить стандартное значение
    const ctrl = findNativeControl(node);
    if (ctrl) {
        return readDomValue(ctrl);
    }
    else {
        // Резервный путь: чтение из текстовых контейнеров
        if (node.dataset.key && isContentTag(node)) {
            const s = readContentTagValue(node);
            return s.trim() === '' ? null : s;
        }
        console.error('[getMainValue] Не найден контрол');
    }
    return undefined;
}
/**
 * Установить «основное значение» для элемента:
 * 1) через виджеты (WithMainValue / WithMultipleValues)
 * 2) через data-val/data-value
 * 3) стандартный путь (аналог jQuery): checkbox/radio/select/textarea/input
 *
 * Возвращает true, если значение было применено.
 * События не генерирует.
 */
function setMainValue(node, value) {
    const widgets = Widget.allOn(node);
    // Пробуем установить через виджеты
    for (const w of widgets) {
        if (isWithMainValue(w)) {
            if (w.setValue(value))
                return true;
        }
        if (isWithMultipleValues(w)) {
            const key = node.dataset.key;
            if (!key)
                continue;
            w.setValues({ [key]: value });
            return true;
        }
    }
    // Работаем со спец атрибутами контейнера если есть
    if (node.hasAttribute('data-value')) {
        node.setAttribute('data-value', String(value ?? ''));
        return true;
    }
    if (node.hasAttribute('data-val')) {
        node.setAttribute('data-val', String(value ?? ''));
        return true;
    }
    if (shouldSkipByClass(node))
        return false;
    // Стандартный путь(как в jQuery)
    const ctrl = findNativeControl(node);
    if (ctrl) {
        // input
        if (ctrl instanceof HTMLInputElement) {
            const type = (ctrl.type || '').toLowerCase();
            if (type === 'checkbox') {
                ctrl.checked = parseCheckboxValue(value);
                return true;
            }
            else if (type === 'radio') {
                // Учитываем группу по name: если значение совпадает — делаем checked
                const valStr = String(value ?? '');
                if (!ctrl.name) {
                    ctrl.checked = (ctrl.value === valStr);
                    return true;
                }
                const scope = ctrl.closest('form') ?? document;
                const radios = scope.querySelectorAll(`input[type="radio"][name="${escName(ctrl.name)}"]`);
                radios.forEach(r => { r.checked = (r.value === valStr); });
                return true;
            }
            else if (type === 'button') {
                // кнопка не является источником/приёмником данных
                return false;
            }
            else if (type === 'password') {
                // пароль всегда тримим
                ctrl.value = String(value ?? '').trim();
                return true;
            }
            // text/number/... → строка
            ctrl.value = String(value ?? '');
            return true;
        }
        // select
        if (ctrl instanceof HTMLSelectElement) {
            if (ctrl.multiple) {
                const list = Array.isArray(value)
                    ? value
                    : parseBraceOrCsv(value);
                const set = new Set(list.map(String));
                Array.from(ctrl.options).forEach(opt => {
                    opt.selected = set.has(opt.value);
                });
                return true;
            }
            else {
                const s = String(value ?? '');
                if (s === '') {
                    // выбираем option с пустым value, если она есть
                    const emptyOpt = Array.from(ctrl.options)
                        .find(o => o.value === '');
                    if (emptyOpt) {
                        emptyOpt.selected = true;
                    }
                    else {
                        ctrl.selectedIndex = -1;
                    }
                }
                else {
                    ctrl.value = s;
                    if (ctrl.value !== s) {
                        // если такой option отсутствует — снимем выбор
                        ctrl.selectedIndex = -1;
                    }
                }
                return true;
            }
        }
        // textarea
        if (ctrl instanceof HTMLTextAreaElement) {
            ctrl.value = String(value ?? '');
            return true;
        }
    }
    // Резервный путь: запись в текстовые контейнеры
    if (node.dataset.key && isContentTag(node)) {
        writeContentTagValue(node, value);
        return true;
    }
    return false;
}
function parseCheckboxValue(value) {
    if (value === null || value === undefined)
        return false;
    if (typeof value === "boolean")
        return value;
    if (typeof value === "number")
        return value > 0;
    if (typeof value === "string") {
        const v = value.trim().toLowerCase();
        if (v === "true" || v === "on" || v === "+")
            return true;
        if (v === "false" || v === "off" || v === "-")
            return false;
        // пробуем как число
        const num = Number(v);
        if (!isNaN(num))
            return num > 0;
        return false;
    }
    return Boolean(value);
}
// TODO: Стоит ли тут проверять на isEmptyGuid?
/** Семантика присутствия для multiple: не undefined, не null, не '' и не 0 */
const isPresentMultiple = (v) => v !== undefined
    && v !== null
    && !(typeof v === "string" && v === "");
/**
 * Проверка «есть ли основное значение» у элемента.
 * Правила:
 *  - для WithMainValue → используем w.hasValue()
 *  - для WithMultipleValues → берём data-key, достаём map[key] и проверяем по правилам multiple
 *
 * Перебор виджетов осуществляется до 1го который смог выполнить проверку
 *
 * @returns
 * true | false - как результат проверки (если проверка состоялась)
 * undefined - если проверка не применима
 */
function hasMainValue(node) {
    const widgets = Widget.allOn(node);
    if (widgets.length > 0) {
        for (const w of widgets) {
            if (isWithMainValue(w)) {
                return w.hasValue();
            }
            if (isWithMultipleValues(w)) {
                const key = node.dataset.key;
                if (!key)
                    continue;
                const map = w.getValues();
                if (map && key in map) {
                    return isPresentMultiple(map[key]);
                }
            }
        }
    }
    // 1) data-value на контейнере
    if (node.hasAttribute('data-value')) {
        return node.getAttribute('data-value') !== '';
    }
    else if (node.hasAttribute('data-val')) {
        return node.getAttribute('data-val') !== '';
    }
    if (shouldSkipByClass(node))
        return undefined;
    // 2) Нативные элементы управления
    const ctrl = findNativeControl(node);
    if (ctrl) {
        // checkbox
        if (ctrl instanceof HTMLInputElement) {
            const type = (ctrl.type || '').toLowerCase();
            if (type === 'checkbox') {
                return ctrl.checked;
            }
            if (type === 'radio') {
                const name = ctrl.name;
                if (!name) {
                    // одиночная радио-кнопка без группы — значение «есть», только если она выбрана
                    return ctrl.checked;
                }
                // есть группа: «есть значение», если в группе есть выбранная
                const scope = ctrl.closest('form') ?? document;
                const checked = scope.querySelector(`input[type="radio"][name="${escName(name)}"]:checked`);
                return !!checked;
            }
        }
        // select (multiple/single)
        if (ctrl instanceof HTMLSelectElement) {
            if (ctrl.multiple) {
                return ctrl.selectedOptions.length > 0;
            }
            else {
                // jQuery-совместная семантика: выбранный value не пуст
                if (ctrl.selectedIndex < 0)
                    return false;
                const v = ctrl.value;
                return !(v == null || v === '');
            }
        }
        // Дополнительно: input/textarea — непустая строка (разумный дефолт)
        if (ctrl instanceof HTMLInputElement || ctrl instanceof HTMLTextAreaElement) {
            return ctrl.value.trim() !== '';
        }
    }
    if (node.dataset.key && isContentTag(node)) {
        return hasContentTagValue(node); // внутри trim()
    }
    return undefined;
}
/**
 * Нормализует значение в массив строк
 *  null/undefined → []
 *  "a,b" → ["a","b"]  (режем по sep, тримим и убираем пустые)
 * {a}{b} -> ["a","b"]
 *  [" a ", "", "b"] → ["a","b"]
 */
function parseBraceOrCsv(input) {
    if (input == null)
        return [];
    if (Array.isArray(input))
        return input.map(s => String(s).trim()).filter(Boolean);
    const raw = String(input);
    if (raw.startsWith('{') && raw.endsWith('}')) {
        return raw.slice(1, -1).split('}{').map(s => s.trim()).filter(Boolean);
    }
    return raw.split(',').map(s => s.trim()).filter(Boolean);
}
/** Кодирование массивов:
 *  - все элементы числа → "1,2,3"
 *  - все элементы строки и ни одна не содержит запятую → "a,b,c"
 *  - иначе → "{a}{b}{c}" (каждый элемент в фигурные, без разделителей)
 */
function encodeArrayField(arr) {
    if (!arr || arr.length === 0)
        return '';
    // все числа
    if (arr.every(v => typeof v === 'number' && Number.isFinite(v))) {
        return arr.join(',');
    }
    // все строки и без запятых
    if (arr.every(v => typeof v === 'string')) {
        const s = arr;
        if (s.every(x => !x.includes(',')))
            return s.join(',');
    }
    // фолбэк: {a}{b}{c}
    return arr.map(v => `{${String(v)}}`).join('');
}
/** Из Date берём только время (локальное) */
function toTimeOnly(d) {
    return {
        hours: d.getHours(),
        minutes: d.getMinutes(),
        seconds: d.getSeconds() || undefined
    };
}
/** true, если валидный Date */
const isValidDate = (v) => v instanceof Date && !isNaN(v.getTime());
/** true, если объект в форме TimeOnly */
const isTimeOnlyValue = (v) => !!v && typeof v.hours === 'number' && typeof v.minutes === 'number';
/** Преобразовать TimeOnly в ISO-строку на 0001-01-01, секунды/мс = 00 */
const timeOnlyToIso = (t) => {
    const hh = String(Math.max(0, Math.min(23, t.hours))).padStart(2, '0');
    const mm = String(Math.max(0, Math.min(59, t.minutes))).padStart(2, '0');
    return `0001-01-01T${hh}:${mm}:00`;
};
/** Преобразовать одно значение к строке для выгрузки, если это Date/TimeOnly */
const encodeScalarIfDateOrTime = (v) => {
    if (isValidDate(v)) {
        return toIsoNoTz(v);
    }
    if (isTimeOnlyValue(v))
        return timeOnlyToIso(v);
    return v;
};
// Текстовые контейнеры и преобразование <br> <-> \n
const CONTENT_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'p', 'span', 'div', 'b']);
const isContentTag = (el) => !!el && el instanceof HTMLElement && CONTENT_TAGS.has(el.tagName.toLowerCase());
const BR_RE = /<br\s*\/>|<\/\s*br\s*>|<br\s*>/gi;
const br2nl = (html) => html.replace(BR_RE, '\n');
const nl2br = (s) => String(s).replace(/\n/g, '<br>');
const readContentTagValue = (el) => br2nl(el.innerHTML);
const writeContentTagValue = (el, value) => {
    if (value == null)
        el.innerHTML = '';
    else
        el.innerHTML = nl2br(String(value));
};
const hasContentTagValue = (el) => br2nl(el.innerHTML).trim() !== '';
const SKIP_CLASSES = [
    'menuitem',
    'im-view',
    'menu',
    'im-fileupload',
    'im-widgetcollection',
    'im-ddmenu',
    'link-button',
    'tree'
];
/** true, если у элемента есть хоть один класс из списка */
const hasAnyClass = (el, classes) => classes.some(c => el.classList.contains(c));
/** удобная обёртка под наш список */
const shouldSkipByClass = (el) => hasAnyClass(el, SKIP_CLASSES);
/** Пустой guid */
const emptyGuid = '00000000-0000-0000-0000-000000000000';
/**
 * Создает случайный GUID версии 4 в каноническом виде с дефисами.
 * В отличие от crypto.randomUUID работает и вне защищенного контекста — на странице, открытой по HTTP.
 */
function newGuid() {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
// Важно: Guid.ToByteArray() в .NET использует «смешанный endian» для первых 3 полей.
// Эта карта переводит между каноническим GUID (hex-строка) и .NET-порядком байт.
const DOTNET_GUID_BYTE_ORDER = [3, 2, 1, 0, 5, 4, 7, 6, 8, 9, 10, 11, 12, 13, 14, 15];
/**
 * Кодирует GUID ("xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx") в base64url (22 символа, без padding),
 * СОВМЕСТИМО с C#:
 *   Convert.ToBase64String(guid.ToByteArray()).Substring(0, 22).Replace("+", "-").Replace("/", "_")
 *
 * @returns 22-символьный base64url-токен или null при неверном GUID
 */
function compressGuidToBase64Url(guid) {
    const h = guid.trim().toLowerCase().replace(/-/g, "");
    if (!/^[0-9a-f]{32}$/.test(h))
        return null;
    // hex (канон) -> байты в .NET порядке -> бинарная строка -> base64url(22)
    let bin = "";
    for (let i = 0; i < 16; i++) {
        const j = DOTNET_GUID_BYTE_ORDER[i] * 2;
        bin += String.fromCharCode(parseInt(h.slice(j, j + 2), 16));
    }
    return btoa(bin).slice(0, 22).replace(/\+/g, "-").replace(/\//g, "_");
}
/**
 * Декодирует base64url-токен (22 символа) обратно в канонический GUID.
 * СОВМЕСТИМО с C#:
 *   Guid.TryParse(value, out ui) ? ui : new Guid(Convert.FromBase64String(value.Replace("-", "+").Replace("_", "/") + "=="))
 *
 * Принимает:
 * - уже канонический GUID (вернёт нормализованный lower-case)
 * - 22-символьный base64url-токен
 *
 * @returns канонический GUID или null при ошибке
 */
function decompressGuidFromBase64Url(token) {
    token = token.trim();
    if (!token)
        return null;
    // если уже GUID — просто нормализуем
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(token))
        return token.toLowerCase();
    // ожидаем ровно 22 символа base64url (как в C# UrlEncode)
    if (!/^[A-Za-z0-9\-_]{22}$/.test(token))
        return null;
    try {
        // восстанавливаем base64 (как в C# + "==")
        const bin = atob(token.replace(/-/g, "+").replace(/_/g, "/") + "==");
        if (bin.length !== 16)
            return null;
        // байты (.NET порядок) -> hex
        let hex = "";
        for (let i = 0; i < 16; i++) {
            const b = bin.charCodeAt(i);
            hex += (b < 16 ? "0" : "") + b.toString(16);
        }
        // .NET порядок -> канонический порядок GUID
        let g = "";
        for (let i = 0; i < 16; i++) {
            const j = DOTNET_GUID_BYTE_ORDER[i] * 2;
            g += hex.slice(j, j + 2);
        }
        return (g.slice(0, 8) + "-" +
            g.slice(8, 12) + "-" +
            g.slice(12, 16) + "-" +
            g.slice(16, 20) + "-" +
            g.slice(20));
    }
    catch {
        return null;
    }
}

export { compressGuidToBase64Url, decompressGuidFromBase64Url, emptyGuid, encodeArrayField, encodeScalarIfDateOrTime, encodeValues, escName, getMainValue, getMainValueStr, hasAnyClass, hasMainValue, isWithFilterEntries, isWithItemTitle, isWithMainValue, isWithMultipleValues, isWithValueTitle, newGuid, parseBraceOrCsv, setMainValue, shouldSkipByClass, toIsoNoTz, toTimeOnly };
